import type { ApiError } from '@ismtur/core';

/** A failed API call, carrying the server's own message and field errors. */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
    readonly details: unknown = undefined,
  ) {
    super(message);
  }
}

type Listener = (failure: ApiFailure) => void;
let onUnauthorized: Listener | null = null;

/** The session store registers here to react when the server ends a session. */
export function whenUnauthorized(listener: Listener): void {
  onUnauthorized = listener;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? null : JSON.stringify(body),
    });
  } catch {
    throw new ApiFailure(0, 'network', 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.');
  }

  if (response.status === 204) return undefined as T;

  const data = (await response.json().catch(() => null)) as T | ApiError | null;
  if (!response.ok) {
    const error = (data as ApiError | null)?.error;
    const failure = new ApiFailure(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? 'Beklenmeyen bir yanıt alındı.',
      error?.fields ?? {},
      error?.details,
    );
    if (response.status === 401 && path !== '/auth/login') onUnauthorized?.(failure);
    throw failure;
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
};

/**
 * Uploads one file with progress. fetch cannot report upload progress, so this
 * uses XMLHttpRequest; errors come back as the same ApiFailure.
 */
export function upload<T>(
  path: string,
  form: FormData,
  onProgress: (fraction: number) => void,
): { promise: Promise<T>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', `/api${path}`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* empty or non-JSON body */
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data as T);
      const error = (data as ApiError | null)?.error;
      const failure = new ApiFailure(
        xhr.status,
        error?.code ?? 'unknown',
        error?.message ?? 'Dosya yüklenemedi.',
        error?.fields ?? {},
        error?.details,
      );
      if (xhr.status === 401) onUnauthorized?.(failure);
      reject(failure);
    };
    xhr.onerror = () => reject(new ApiFailure(0, 'network', 'Bağlantı koptu; dosya yüklenemedi.'));
    xhr.onabort = () => reject(new ApiFailure(0, 'aborted', 'Yükleme iptal edildi.'));
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}

/** Builds a query string, leaving out empty values. */
export function query(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '' && value !== false) {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}
